/**
 * CandidatePool — Browse and add candidates to the pool.
 * Instant job matching on add.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Users, Plus, Search, X, CheckCircle2, Loader2, Star, ChevronDown } from 'lucide-react';
import { apiGet, apiPost, apiPut, apiDel } from '../../../lib/api';
import { toast } from 'sonner';

const EMPTY = {
  name: '', email: '', phone: '', current_role: '', current_employer: '',
  years_experience: '', skills: '', education: '', location: '',
  expected_ctc: '', notes: '', source: 'Manual',
};

export default function CandidatePool() {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [lastMatches, setLastMatches] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiGet('/hr/recruitment/pool', search ? { q: search } : {});
      setCandidates(r.items || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      const r = await apiPost('/hr/recruitment/pool', form);
      toast.success(`${r.candidate.name} added to pool`);
      if (r.instant_matches?.length) setLastMatches(r.instant_matches);
      setShowForm(false); setForm(EMPTY); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remove ${name} from pool?`)) return;
    try { await apiDel(`/hr/recruitment/pool/${id}`); load(); } catch { toast.error('Failed'); }
  };

  const REC_BADGE = { 'Strong Hire': 'bg-emerald-100 text-emerald-700', 'Hire': 'bg-teal-100 text-teal-700', 'Maybe': 'bg-amber-100 text-amber-700', 'No': 'bg-rose-100 text-rose-700' };

  return (
    <div className="space-y-5" data-testid="candidate-pool">
      {/* Instant match result */}
      {lastMatches && (
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-bold text-teal-800 flex items-center gap-1.5"><Star size={14} /> {lastMatches.length} instant job match(es) found!</p>
            <button onClick={() => setLastMatches(null)}><X size={14} className="text-teal-500" /></button>
          </div>
          <div className="flex flex-wrap gap-2">
            {lastMatches.map((m, i) => (
              <span key={i} className="text-xs bg-white border border-teal-200 rounded px-2 py-1">
                {m.role_title} — <strong>{m.match_score}% match</strong>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-display font-bold text-lg flex items-center gap-2"><Users size={18} /> Candidate Pool</h2>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-2.5 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name / skill…"
              className="pl-8 pr-3 py-2 border border-slate-300 rounded-sm text-sm w-52" />
          </div>
          <button onClick={() => setShowForm(true)} className="flex items-center gap-1.5 px-3 py-2 bg-[#1F7A6E] text-white rounded-sm text-xs font-bold" data-testid="pool-add-btn">
            <Plus size={14} /> Add Candidate
          </button>
        </div>
      </div>

      {/* Add form */}
      {showForm && (
        <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-3" data-testid="pool-add-form">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-bold">Add to Candidate Pool</h3>
            <button onClick={() => setShowForm(false)}><X size={16} className="text-slate-400" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              ['name', 'Full Name *', 'text'],
              ['email', 'Email', 'email'],
              ['phone', 'Phone', 'text'],
              ['current_role', 'Current Role', 'text'],
              ['current_employer', 'Current Employer', 'text'],
              ['years_experience', 'Years Experience', 'number'],
              ['location', 'Location', 'text'],
              ['expected_ctc', 'Expected CTC', 'text'],
            ].map(([k, label, type]) => (
              <div key={k}>
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</label>
                <input type={type} value={form[k]} onChange={e => setForm(f => ({...f, [k]: e.target.value}))}
                  className="w-full mt-0.5 px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              </div>
            ))}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Skills (comma-separated)</label>
            <input value={form.skills} onChange={e => setForm(f => ({...f, skills: e.target.value}))}
              placeholder="React, Python, Project Management…"
              className="w-full mt-0.5 px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Education</label>
            <input value={form.education} onChange={e => setForm(f => ({...f, education: e.target.value}))}
              className="w-full mt-0.5 px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Notes</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} rows={2}
              className="w-full mt-0.5 px-3 py-2 border border-slate-300 rounded-sm text-sm resize-none" />
          </div>
          <div className="flex gap-2 pt-1 border-t border-slate-100">
            <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm border border-slate-300 rounded-sm">Cancel</button>
            <button onClick={save} disabled={saving} className="px-4 py-2 text-sm font-bold bg-[#1F7A6E] text-white rounded-sm disabled:opacity-40 flex items-center gap-1.5" data-testid="pool-save-btn">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
              {saving ? 'Saving…' : 'Add to Pool'}
            </button>
          </div>
        </div>
      )}

      {/* Candidate list */}
      {loading ? <div className="py-8 text-center text-slate-400 text-sm">Loading…</div> : (
        <div className="space-y-2">
          {candidates.map(c => (
            <div key={c.id} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`pool-row-${c.id}`}>
              <div className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-slate-50" onClick={() => setExpanded(expanded === c.id ? null : c.id)}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="min-w-0">
                    <p className="font-display font-bold text-sm truncate">{c.name}</p>
                    <p className="text-xs text-slate-500 truncate">{c.current_role}{c.current_employer ? ` @ ${c.current_employer}` : ''} · {c.years_experience}y exp</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-2">
                  {c.available !== false && <span className="text-[9px] bg-emerald-100 text-emerald-700 font-bold px-1.5 py-0.5 rounded-full">Available</span>}
                  <ChevronDown size={14} className={`text-slate-400 transition-transform ${expanded === c.id ? 'rotate-180' : ''}`} />
                </div>
              </div>
              {expanded === c.id && (
                <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-2">
                  <div className="flex flex-wrap gap-1.5">
                    {(c.skills || []).map(s => <span key={s} className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{s}</span>)}
                  </div>
                  {c.education && <p className="text-xs text-slate-500">Education: {c.education}</p>}
                  {c.location && <p className="text-xs text-slate-500">Location: {c.location}</p>}
                  {c.email && <p className="text-xs text-slate-500">{c.email} {c.phone ? `· ${c.phone}` : ''}</p>}
                  {c.notes && <p className="text-xs text-slate-500 italic">{c.notes}</p>}
                  <div className="flex gap-2 pt-1">
                    <button onClick={() => remove(c.id, c.name)} className="text-xs text-rose-600 hover:underline">Remove from pool</button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {!candidates.length && (
            <div className="text-center py-10 text-slate-400">
              <Users size={36} className="mx-auto mb-2 text-slate-300" />
              <p className="text-sm">No candidates in pool yet. Add one above.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
